<!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Activity heatmap: 52-week task completion grid -->
<template>
    <div class="activity-heatmap">
        <!-- Non-premium: upgrade upsell -->
        <div v-if="!isPremium" class="py-3">
            <p class="text-muted mb-2">
                The Activity Heatmap gives you a full year of task completion history at a glance.
                Each cell represents a day — darker cells mean more tasks completed. Spot your most
                productive periods and see your best week of the year highlighted below.
            </p>
            <div class="mt-3" style="text-align: right;">
                <b-button variant="primary" size="sm" @click="$emit('upgrade-clicked')">
                    <b-icon icon="star-fill" class="me-2" />
                    Upgrade to View
                </b-button>
            </div>
        </div>

        <!-- Premium + loading: skeleton -->
        <div v-else-if="loading" class="bg-light heatmap-skeleton"></div>

        <!-- Premium + data -->
        <div v-else class="heatmap-wrapper">
            <div class="heatmap-layout">

                <!-- Day-of-week labels -->
                <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Grid rows match heatmap exactly; padding-top offsets for month labels -->
                <div class="day-labels" aria-hidden="true">
                    <span>Sun</span>
                    <span>Mon</span>
                    <span>Tue</span>
                    <span>Wed</span>
                    <span>Thu</span>
                    <span>Fri</span>
                    <span>Sat</span>
                </div>

                <!-- Month labels + grid -->
                <div class="grid-area">
                    <div class="grid-scroll">
                        <div class="month-labels" aria-hidden="true">
                            <div
                                v-for="(label, idx) in monthLabels"
                                :key="idx"
                                class="month-label"
                                :style="{ gridColumnStart: label.weekIndex + 1 }"
                            >{{ label.label }}</div>
                        </div>
                        <div class="heatmap-grid" role="grid" aria-label="Task completion heatmap">
                            <template v-for="(week, wIdx) in weeks" :key="wIdx">
                                <div
                                    v-for="(day, dIdx) in week"
                                    :key="dIdx"
                                    class="heatmap-cell"
                                    :class="{ 'heatmap-cell--clickable': day.count > 0 && !day.isFuture }"
                                    :style="{ backgroundColor: cellColor(day.count), gridColumn: wIdx + 1, gridRow: dIdx + 1 }"
                                    :title="cellTitle(day)"
                                    role="gridcell"
                                    :aria-label="cellTitle(day)"
                                    :tabindex="day.count > 0 && !day.isFuture ? 0 : -1"
                                    @click="day.count > 0 && !day.isFuture && $emit('cell-clicked', day)"
                                    @keydown.enter.prevent="day.count > 0 && !day.isFuture && $emit('cell-clicked', day)"
                                    @keydown.space.prevent="day.count > 0 && !day.isFuture && $emit('cell-clicked', day)"
                                ></div>
                            </template>
                        </div>
                    </div>

                    <!-- Footer: best week left, legend right -->
                    <div v-if="heatmapData" class="heatmap-footer">
                        <span class="heatmap-stats text-muted">
                            <span v-if="heatmapData.bestWeek && heatmapData.bestWeek.count > 0">
                                Best week: {{ heatmapData.bestWeek.count }} completed ({{ bestWeekRange }})
                            </span>
                        </span>
                        <span class="heatmap-legend" aria-label="Color scale: less to more completions">
                            <span class="text-muted">Less</span>
                            <span
                                v-for="(color, idx) in legendColors"
                                :key="idx"
                                class="legend-cell"
                                :style="{ backgroundColor: color }"
                            ></span>
                            <span class="text-muted">More</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    name: 'ActivityHeatmap',

    emits: ['upgrade-clicked', 'cell-clicked'],

    props: {
        heatmapData: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        isPremium: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            legendColors: ['#e5e7eb', '#bbf7d0', '#4ade80', '#16a34a', '#14532d']
        }
    },

    computed: {
        weeks() {
            const result = []
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Build grid in UTC so isFuture, keys, and display all align with backend UTC-day bucketing
            const today = moment.utc().startOf('day')
            // System convention: calendar weeks start on Sunday (US standard per CLAUDE.md)
            const gridEnd = today.clone().day(6)               // explicit Saturday — endOf('week') is locale-dependent in Moment.js
            const gridStart = gridEnd.clone().subtract(6, 'days').subtract(51, 'weeks')  // Sunday 51 weeks before gridEnd — startOf('week') is locale-dependent

            let current = gridStart.clone()
            while (current.isSameOrBefore(gridEnd, 'day')) {
                const week = []
                for (let d = 0; d < 7; d++) {
                    const day = current.clone().add(d, 'days')
                    const dayMs = day.valueOf()
                    const count = this.heatmapData?.days?.[dayMs.toString()] || 0
                    week.push({ ms: dayMs, count, date: day.clone(), isFuture: day.isAfter(today) })
                }
                result.push(week)
                current.add(1, 'week')
            }
            return result
        },

        monthLabels() {
            const labels = []
            let lastMonth = -1
            this.weeks.forEach((week, wIdx) => {
                // Anchor to the first day in the week that starts a new month (GitHub-style: label aligns with where the month actually begins)
                const pivot = week.find(day => day.date.month() !== lastMonth)
                if (!pivot) return
                lastMonth = pivot.date.month()
                labels.push({ label: pivot.date.format('MMM'), weekIndex: wIdx })
            })
            return labels
        },

        bestWeekRange() {
            if (!this.heatmapData?.bestWeek?.startMs) return ''
            // Use UTC formatting — startMs is UTC midnight; local formatting shifts it one day back for UTC-offset users
            const start = moment.utc(this.heatmapData.bestWeek.startMs)
            const end = start.clone().add(6, 'days')
            if (start.month() === end.month()) {
                return `${start.format('MMM D')}–${end.format('D')}`
            }
            return `${start.format('MMM D')} – ${end.format('MMM D')}`
        }
    },

    methods: {
        cellColor(count) {
            if (count === 0) return '#e5e7eb'
            if (count <= 2) return '#bbf7d0'
            if (count <= 5) return '#4ade80'
            if (count <= 10) return '#16a34a'
            return '#14532d'
        },

        cellTitle(day) {
            if (day.isFuture) return moment.utc(day.ms).format('MMMM D, YYYY')
            const noun = day.count === 1 ? 'task' : 'tasks'
            return `${day.count} ${noun} completed on ${moment.utc(day.ms).format('MMMM D, YYYY')}`
        }
    }
}
</script>

<style scoped>
.heatmap-skeleton {
    height: 160px;
    border-radius: 4px;
}

.heatmap-wrapper {
    width: 100%;
}

.heatmap-layout {
    display: flex;
    gap: 6px;
    align-items: flex-start;
}

/* Day-of-week labels: grid rows match heatmap exactly (20px rows, 2px gaps)
   padding-top = month-label height (10px) + padding-bottom (2px) + margin-bottom (4px) = 16px */
.day-labels {
    display: grid;
    grid-template-rows: repeat(7, 20px);
    gap: 2px;
    padding-top: 16px;
    flex-shrink: 0;
    width: 28px;
    text-align: right;
    font-size: 9px;
    color: #6b7280;
    align-items: center;
}

.grid-area {
    flex: 1;
    min-width: 0;
}

/* Horizontal scroll for narrow viewports */
.grid-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

/* Month labels: fluid columns matching heatmap-grid */
.month-labels {
    display: grid;
    grid-template-columns: repeat(52, 1fr);
    gap: 2px;
    margin-bottom: 4px;
    min-width: 600px;
}

.month-label {
    font-size: 10px;
    color: #6b7280;
    white-space: nowrap;
    line-height: 1;
    padding-bottom: 2px;
}

/* Heatmap grid: fluid columns fill available width */
.heatmap-grid {
    display: grid;
    grid-template-columns: repeat(52, 1fr);
    grid-template-rows: repeat(7, 20px);
    gap: 2px;
    min-width: 600px;
}

.heatmap-cell {
    border-radius: 2px;
    cursor: default;
    transition: opacity 0.1s ease;
}

.heatmap-cell--clickable {
    cursor: pointer;
}

.heatmap-cell:hover {
    opacity: 0.75;
}

/* Footer: stats on left, legend on right */
.heatmap-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    flex-wrap: wrap;
    gap: 8px;
}

.heatmap-stats {
    font-size: 12px;
    line-height: 1.4;
}

.heatmap-legend {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #6b7280;
    flex-shrink: 0;
}

.legend-cell {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
}

@media (prefers-reduced-motion: reduce) {
    .heatmap-cell { transition: none; }
}
</style>
